import React, { useEffect, useRef, useState } from 'react';
import './SidebarNav.css';

const HomeIcon = () => (
  <svg className="sidebar-nav-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
  </svg>
);

const ChartIcon = () => (
  <svg className="sidebar-nav-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  </svg>
);

const ToolsIcon = () => (
  <svg className="sidebar-nav-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.7 6.3a2 2 0 11-2.8-2.8l2.1-2.1a2 2 0 112.8 2.8l-2.1 2.1zM7 17l-3 3m0 0l-2-2m2 2l2 2m9-13l-8 8m0 0L7 17m2 2L7 17" />
  </svg>
);

const UsersIcon = () => (
  <svg className="sidebar-nav-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);

const LogoutIcon = () => (
  <svg className="sidebar-nav-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
  </svg>
);

const ChevronLeftIcon = () => (
  <svg className="sidebar-toggle-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg className="sidebar-toggle-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
  </svg>
);

const SIDEBAR_MODE_KEY = 'portalSidebarMode';

const SidebarNav = ({
  activePage,
  onHome = () => {},
  onCharts = () => {},
  onTools = () => {},
  onUsers = () => {},
  onLogout = () => {},
}) => {
  const [isMinimized, setIsMinimized] = useState(() => localStorage.getItem(SIDEBAR_MODE_KEY) === 'minimized');
  const sidebarRef = useRef(null);

  const navItems = [
    { id: 'home', label: 'Capsule Processing Progress', icon: <HomeIcon />, onClick: onHome },
    { id: 'charts', label: 'Statistics Chart', icon: <ChartIcon />, onClick: onCharts },
    { id: 'tools', label: 'Tools', icon: <ToolsIcon />, onClick: onTools },
    { id: 'users', label: 'Users', icon: <UsersIcon />, onClick: onUsers },
    { id: 'logout', label: 'Logout', icon: <LogoutIcon />, onClick: onLogout },
  ];

  useEffect(() => {
    localStorage.setItem(SIDEBAR_MODE_KEY, isMinimized ? 'minimized' : 'detailed');
  }, [isMinimized]);

  useEffect(() => {
    const updateSidebarWidth = () => {
      if (!sidebarRef.current) return;
      const width = Math.ceil(sidebarRef.current.getBoundingClientRect().width);
      document.documentElement.style.setProperty('--portal-sidebar-width', `${width}px`);
    };

    updateSidebarWidth();
    window.addEventListener('resize', updateSidebarWidth);

    let observer;
    if (typeof ResizeObserver !== 'undefined' && sidebarRef.current) {
      observer = new ResizeObserver(updateSidebarWidth);
      observer.observe(sidebarRef.current);
    }

    return () => {
      window.removeEventListener('resize', updateSidebarWidth);
      if (observer) observer.disconnect();
    };
  }, [isMinimized, activePage]);

  return (
    <aside
      ref={sidebarRef}
      className={`sidebar shared-sidebar ${isMinimized ? 'sidebar-minimized' : 'sidebar-detailed'}`}
    >
      <div className="sidebar-brand">
        {!isMinimized && <span className="sidebar-brand-text">Web Portal</span>}
        <button
          type="button"
          className="sidebar-brand-toggle"
          onClick={() => setIsMinimized((prev) => !prev)}
          title={isMinimized ? 'Expand navigation' : 'Collapse navigation'}
          aria-label={isMinimized ? 'Expand navigation' : 'Collapse navigation'}
        >
          {isMinimized ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </button>
      </div>

      <ul className="sidebar-nav">
        {navItems.map((item) => {
          const isActive = item.id === activePage;
          const isBottomItem = item.id === 'logout';
          return (
            <li className={`sidebar-nav-item ${isBottomItem ? 'sidebar-nav-item-bottom' : ''}`} key={item.id}>
              {isActive && <div className="active-nav-indicator"></div>}
              <button
                onClick={item.onClick}
                className={`sidebar-nav-link ${isActive ? 'active' : ''}`}
                title={isMinimized ? item.label : undefined}
                aria-label={item.label}
              >
                {item.icon}
                {!isMinimized && <span>{item.label}</span>}
              </button>
            </li>
          );
        })}
      </ul>
    </aside>
  );
};

export default SidebarNav;
